<template>
  <v-data-table
    :headers="headers"
    :items="customers"
    :items-per-page="100"
    :options.sync="options"
    :server-items-length="total"
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'mdi-arrow-collapse-left',
      lastIcon: 'mdi-arrow-collapse-right',
      prevIcon: 'mdi-arrow-left',
      nextIcon: 'mdi-arrow-right',
      itemsPerPageOptions: [10, 25, 50, 100],
    }"
    :loading="loading"
    multi-sort
    class="elevation-1"
  >
    <template v-slot:top>
      <v-toolbar
        flat
        color="white"
      >
        <v-toolbar-title>{{ $t("lbl.customers") }}</v-toolbar-title>
        <v-divider
          class="mx-4"
          inset
          vertical
        />
        <v-spacer />
        <v-dialog
          v-model="dialog"
          max-width="600px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="auth(['manageCustomers', 'modifyResellerCustomers', 'modifyRetailerCustomers'])"
              color="primary"
              class="mb-2"
              v-bind="attrs"
              v-on="on"
              :disabled="disable"
            >{{
              $t("lbl.newCustomer")
            }}</v-btn>
          </template>
          <v-form
            ref="form"
            v-model="valid"
          >
            <v-card>
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                    >
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="rules.name"
                        :label="$t('lbl.name')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                    >
                      <v-text-field
                        v-model="editedItem.taxNo"
                        :rules="rules.taxNo"
                        :label="$t('lbl.taxNo')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                    >
                      <v-text-field
                        v-model="editedItem.email"
                        :rules="rules.email"
                        :label="$t('lbl.email')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                    >
                      <v-text-field
                        v-model="editedItem.phone"
                        :rules="rules.phone"
                        :label="$t('lbl.phone')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="12"
                      md="12"
                    >
                      <v-text-field
                        v-model="editedItem.address"
                        :rules="rules.address"
                        :label="$t('lbl.address')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                    >
                      <v-autocomplete
                        v-model="editedItem.city"
                        :items="cities"
                        :rules="rules.city"
                        :label="$t('lbl.city')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                    >
                      <v-autocomplete
                        v-model="editedItem.district"
                        :items="districts"
                        :rules="rules.district"
                        :label="$t('lbl.district')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="12"
                      md="12"
                    >
                      <v-text-field
                        v-model="editedItem.description"
                        :rules="rules.description"
                        :label="$t('lbl.description')"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="reset"
                >{{ $t("lbl.clear") }}</v-btn>
                <v-spacer />
                <v-btn
                  color="blue darken-1"
                  text
                  @click="dialog = false"
                >{{
                  $t("lbl.cancel")
                }}</v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :disabled="!valid"
                  @click="save"
                >{{
                  $t("lbl.save")
                }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>
      </v-toolbar>
      <v-container>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-text-field
              outlined
              dense
              hide-details
              v-model="filter.search"
              :label="$t('lbl.customerSearch')"
              @keydown.enter="initialize(true)"
            />
          </v-col>
          <v-col
            v-if="auth('get')"
            cols="12"
            sm="6"
            md="2"
          >
            <v-select
              outlined
              dense
              hide-details
              clearable
              v-model="filter.reseller"
              :items="resellers"
              item-text="name"
              item-value="id"
              :label="$t('lbl.reseller')"
              @change="initialize(true)"
            />
          </v-col>
          <v-col
            v-if="auth(['get', 'fetchResellerCustomers'])"
            cols="12"
            sm="6"
            md="2"
          >
            <v-autocomplete
              outlined
              dense
              hide-details
              clearable
              v-model="filter.retailer"
              :items="retailers"
              item-text="name"
              item-value="id"
              :label="$t('lbl.retailer')"
              @change="initialize(true)"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-autocomplete
              outlined
              dense
              hide-details
              clearable
              v-model="filter.city"
              :items="cities"
              :label="$t('lbl.city')"
              @change="initialize(true)"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-datetime-picker
              v-model="filter.start"
              :label="$t('lbl.start')"
              :clearText="$t('lbl.clear')"
              :okText="$t('lbl.ok')"
              :textFieldProps="{
                outlined: true,
                dense: true,
                hideDetails: true,
                clearable: true,
              }"
              :datePickerProps="{ locale: $i18n.locale, 'no-title': true }"
              :timePickerProps="{ format: '24hr', 'no-title': true }"
              @input="initialize(true)"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-datetime-picker
              v-model="filter.end"
              :label="$t('lbl.end')"
              :clearText="$t('lbl.clear')"
              :okText="$t('lbl.ok')"
              :textFieldProps="{
                outlined: true,
                dense: true,
                hideDetails: true,
                clearable: true,
              }"
              :datePickerProps="{ locale: $i18n.locale, 'no-title': true }"
              :timePickerProps="{ format: '24hr', 'no-title': true }"
              @input="initialize(true)"
            />
          </v-col>
          <v-spacer />
          <v-col
            cols="12"
            sm="12"
            md="1"
          >
            <v-btn
              block
              color="warning"
              @click="refresh"
            >
              <v-icon>mdi-refresh-circle</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template v-slot:[`item.date`]="{ item }">
      <span>
        {{ item.date && $luxon.fromISO(item.date).toLocaleString(fmt) }}
      </span>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon
        class="mr-2"
        :disabled="disable"
        @click="editItem(item)"
      > mdi-pencil </v-icon>
      <v-icon
        :disabled="disable"
        @click="deleteItem(item)"
      > mdi-delete </v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { mapActions } from "vuex";
import { DateTime } from "luxon";
import { cities, districts } from "../common/turkiye";
import {
  required,
  email,
  length,
  minLength,
  maxLength,
  numeric,
} from "../common/validation";

export default {
  data: () => ({
    dialog: false,
    disable: false,
    valid: true,
    loading: true,
    options: {},
    total: 0,
    customers: [],
    resellers: [],
    retailers: [],
    fmt: DateTime.DATETIME_SHORT,
    editedIndex: -1,
    editedItem: {
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      district: "",
      taxNo: "",
      description: "",
    },
    defaultItem: {
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      district: "",
      taxNo: "",
      description: "",
    },
    rules: {
      name: [required, maxLength(64)],
      email: [required, email],
      phone: [required, numeric, length(10)],
      address: [required, maxLength(128)],
      city: [required],
      district: [required],
      taxNo: [required, numeric, minLength(10), maxLength(11)],
      description: [maxLength(64)],
    },
    filter: {
      search: null,
      city: null,
      start: null,
      end: null,
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("lbl.newCustomer")
        : this.$t("lbl.editCustomer");
    },
    headers() {
      const columns = [
        { text: this.$t("lbl.code"), value: "ccode" },
        { text: this.$t("lbl.name"), value: "name" },
        { text: this.$t("lbl.email"), value: "email" },
        { text: this.$t("lbl.phone"), value: "phone" },
        { text: this.$t("lbl.address"), value: "address" },
        { text: this.$t("lbl.city"), value: "city" },
        { text: this.$t("lbl.district"), value: "district" },
        { text: this.$t("lbl.taxNo"), value: "taxNo" },
      ];
      if (this.auth("manageCustomers")) {
        columns.push({
          text: this.$t("lbl.reseller"),
          value: "reseller.name",
        });
      }
      if (this.auth(["manageCustomers", "modifyResellerCustomers"])) {
        columns.push({
          text: this.$t("lbl.retailer"),
          value: "retailer.name",
        });
      }
      columns.push(
        { text: this.$t("lbl.description"), value: "description" },
        { text: this.$t("lbl.date"), value: "date", width: "10%" },
      );

      if (this.auth(["manageCustomers", "modifyResellerCustomers", "modifyRetailerCustomers"])) {
        columns.push({
          text: this.$t("lbl.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        });
      }
      return columns;
    },
    cities() {
      return cities;
    },
    districts() {
      if (this.editedItem.city) {
        return districts[this.editedItem.city];
      }
      return [];
    },
  },

  watch: {
    dialog(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close();
    },
    options: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("customer", [
      "findCustomers",
      "getCustomer",
      "saveCustomer",
      "deleteCustomer",
      "findResellerCustomers",
      "saveResellerCustomer",
      "deleteResellerCustomer",
      "findRetailerCustomers",
      "saveRetailerCustomer",
      "deleteRetailerCustomer",
    ]),
    ...mapActions("reseller", ["findResellers"]),
    ...mapActions("retailer", ["findRetailers", "findResellerRetailers"]),

    getRequester(key) {
      let requester;
      switch (key) {
        case "find":
          if (this.auth("getCustomers")) {
            requester = this.findCustomers;
          } else if (this.auth("fetchResellerCustomers")) {
            requester = this.findResellerCustomers;
          } else if (this.auth("fetchRetailerCustomers")) {
            requester = this.findRetailerCustomers;
          }
          break;
        case "save":
          if (this.auth("manageCustomers")) {
            requester = this.saveCustomer;
          } else if (this.auth("modifyResellerCustomers")) {
            requester = this.saveResellerCustomer;
          } else if (this.auth("modifyRetailerCustomers")) {
            requester = this.saveRetailerCustomer;
          }
          break;
        case "delete":
          if (this.auth("manageCustomers")) {
            requester = this.deleteCustomer;
          } else if (this.auth("modifyResellerCustomers")) {
            requester = this.deleteResellerCustomer;
          } else if (this.auth("modifyRetailerCustomers")) {
            requester = this.deleteRetailerCustomer;
          }
          break;
        default:
          break;
      }
      return requester;
    },

    async initialize(search) {
      this.loading = true;
      if (search) {
        this.options.page = 1;
      }
      const filters = this.getFilters(this.options, this.filter);
      try {
        const requester = this.getRequester("find");
        const { results, totalResults } = await requester(filters);
        this.customers = results;
        this.total = totalResults;
      } catch (error) {
        this.$toasted.error(this.getMessageText(error.message));
      } finally {
        this.loading = false;
      }

      if (this.auth("get")) {
        try {
          const { results } = await this.findResellers({
            limit: 0,
            select: "name",
          });
          this.resellers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }
      }

      if (this.auth("get")) {
        try {
          const { results } = await this.findRetailers({
            limit: 0,
            select: "name",
          });
          this.retailers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }
      }
      if (this.auth("fetchResellerRetailers")) {
        try {
          const { results } = await this.findResellerRetailers({
            limit: 0,
            select: "name",
          });
          this.retailers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    close() {
      this.reset();
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    editItem(item) {
      this.editedIndex = this.customers.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },

    deleteItem(item) {
      this.$swal({
        html: this.$t("msg.deleteText", {
          item: item.name,
        }),
        icon: "warning",
        showCancelButton: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("lbl.delete"),
        cancelButtonText: this.$t("lbl.cancel"),
      }).then((result) => {
        if (result.value) {
          this.disable = true;
          const requester = this.getRequester("delete");
          requester(item.id)
            .then(() => {
              this.initialize();
              this.$toasted.success(
                this.$t("msg.delete", { item: this.$t("lbl.customer") }),
              );
            })
            .catch((error) => {
              this.$toasted.error(this.getMessageText(error.message));
            })
            .finally(() => {
              this.disable = false;
            });
        }
      });
    },

    save() {
      this.dialog = false;
      this.disable = true;
      const editedItem = { ...this.editedItem };
      const requester = this.getRequester("save");
      requester(editedItem)
        .then(() => {
          this.initialize();
          this.$toasted.success(
            this.$t("msg.save", { item: this.$t("lbl.customer") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        })
        .finally(() => {
          this.disable = false;
        });
    },

    refresh() {
      Object.keys(this.filter).forEach((i) => {
        this.filter[i] = null;
      });
      this.initialize();
    },
  },
};
</script>
